import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { API_PLATFORM_URL, AUTH_CONFIG } from '@core/config/config.service';
import { IAuthConfig } from '@env/IEnvironment';
import { IAuthenticationService, IUserClaims } from '@mynexus/mfe-core';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { catchError } from 'rxjs/operators';
import { UserClinicalCredentials } from '@core/model';

@Injectable()
export class AuthenticationService implements IAuthenticationService {
  private _userPermissions: string[] = [];

  constructor(
    private msalService: MsalService,
    private readonly http: HttpClient,
    @Inject(API_PLATFORM_URL) private readonly apiPlatformUrl: string,
    @Inject(AUTH_CONFIG) private authConfig: IAuthConfig
  ) {}
  get userPermissions(): string[] {
    return this._userPermissions;
  }

  getToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.msalService
        .acquireTokenSilent({
          scopes: this.authConfig.angularConfig.protectedResourceMap[0][1],
          account: this.user
        })
        .subscribe({
          next: ({ accessToken }) => resolve(accessToken),
          error: reject
        });
    });
  }

  get userClaims(): IUserClaims {
    return this.user?.idTokenClaims ? (this.user.idTokenClaims as IUserClaims) : {};
  }

  get userEmail(): string | null {
    const claims = this.userClaims;

    if (claims?.emails) {
      const emails = claims.emails;

      if (emails.length > 0) {
        return emails[0];
      }
    }

    return null;
  }

  get userGroups(): string[] {
    return this.userClaims?.groups || [];
  }

  get token(): string | undefined {
    return this.user?.idToken;
  }

  get user(): AccountInfo {
    return this.msalService.instance.getAllAccounts()[0];
  }

  get userName(): string {
    // tslint:disable-next-line: no-non-null-assertion
    return this.user ? this.user.name! : '';
  }

  checkUserRole(roleName: string): boolean {
    return (this.user.idTokenClaims as any)['groups'].includes(roleName);
  }

  logout(): void {
    this.msalService.logout();
  }

  // TODO: remove after Notes migration to Angular 21
  private userCredentialsCache = new Map<string, Observable<UserClinicalCredentials>>();

  // TODO: remove after Notes migration to Angular 21
  fetchUserProfileCredentialsByEmail(email: string): Observable<UserClinicalCredentials> {
    const url = `${this.apiPlatformUrl}Graph/GetUserProfilesListCredentialsByEmails`;

    return this.http.post<UserClinicalCredentials[]>(url, [email]).pipe(map(([creds]) => creds));
  }

  // TODO: remove after Notes migration to Angular 21
  getUserClinicalCredentialsByEmail(email: string): any {
    if (!this.userCredentialsCache.has(email)) {
      const userClinicalCred = this.fetchUserProfileCredentialsByEmail(email).pipe(
        catchError(() => of({ email: email } as UserClinicalCredentials)),
        shareReplay()
      );

      this.userCredentialsCache.set(email, userClinicalCred);
    }

    return this.userCredentialsCache.get(email);
  }

  getPermissions(): Observable<Record<string, string[]>> {
    return this.http
      .post<Record<string, string[]>>(`${this.apiPlatformUrl}/Permissions`, null)
      .pipe(
        tap(res => {
          this._userPermissions = Object.values(res).flat();
        })
      );
  }
}

